import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { DialogPortal } from '../ui/dialogHost';
import { api, ApiError, fieldIssues } from '../api';
import { trapTab } from '../ui/focus';
import { endPastMidnight, formatDayText, lastDayAfterFirstChange, parseDayText, scopeGroups, type CustomScope } from './fields';
import { addDays, DEFAULT_EQUIPMENT_SOURCE, formatDay, nextTermDay, NO_GEAR_SOURCE, RESERVATION_LABELS, runsOf, type ReservationKind } from './labels';
import { systemGroups, type SystemItem } from '../settings/systems';
import { RowMenu } from '../ui/RowMenu';
import { todayInWarsaw } from './calendar';
import './fields.css';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';

/**
 * Form fields shared by the "Nowe wydarzenie" wizard and the event form (owner remarks 1–9 in
 * docs/uwagi-wlasciciela-1.md): typed or picked days with a big calendar, a client added on the spot,
 * scope pills, "Wstępna / Pewna" with an explanation, a highlighted "Potrzebna wycena" switch.
 */

// ---------- days ----------

const MONTH = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const DAY_NAME = new Intl.DateTimeFormat('pl-PL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const WEEKDAYS = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];
const utc = (day: string) => new Date(`${day}T12:00:00Z`);

/** The 6 × 7 days shown for the month of `month` ('YYYY-MM-01'), Monday first. */
function monthGrid(month: string): string[] {
  const first = utc(month);
  const back = (first.getUTCDay() + 6) % 7;
  const start = addDays(month, -back);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}
const monthOf = (day: string) => `${day.slice(0, 7)}-01`;
const shiftMonth = (month: string, n: number) => {
  const d = utc(month);
  d.setUTCMonth(d.getUTCMonth() + n, 1);
  return d.toISOString().slice(0, 10);
};

/** A large month calendar (owner remark 8: easy to hit, also with a finger). */
function CalendarPop({ label, value, min, onPick, onClose }: { label: string; value: string; min?: string; onPick: (day: string) => void; onClose: () => void }) {
  const today = todayInWarsaw();
  const [month, setMonth] = useState(monthOf(value || min || today));
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button[aria-current="date"], button.today, .cal-pop-grid button:not([disabled])')?.focus({ preventScroll: true });
    // the whole month in sight (on a phone the field may sit low on the screen)
    ref.current?.scrollIntoView?.({ block: 'nearest' });
    const away = (e: PointerEvent) => {
      if (ref.current && !ref.current.parentElement?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [onClose]);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
  };
  return (
    <div className="cal-pop" role="dialog" aria-label={label} ref={ref} onKeyDown={onKey}>
      <div className="cal-pop-head">
        <button type="button" className="btn icon ghost" aria-label="Poprzedni miesiąc" onClick={() => setMonth(shiftMonth(month, -1))}>
          ‹
        </button>
        <strong aria-live="polite">{MONTH.format(utc(month))}</strong>
        <button type="button" className="btn icon ghost" aria-label="Następny miesiąc" onClick={() => setMonth(shiftMonth(month, 1))}>
          ›
        </button>
        <DialogLayer onClose={onClose} />
      </div>
      <div className="cal-pop-grid">
        {WEEKDAYS.map((w) => (
          <span key={w} className="cal-pop-wd" aria-hidden="true">
            {w}
          </span>
        ))}
        {monthGrid(month).map((day) => (
          <button
            key={day}
            type="button"
            className={`${day.slice(0, 7) === month.slice(0, 7) ? '' : 'other'}${day === today ? ' today' : ''}`}
            aria-current={day === value ? 'date' : undefined}
            aria-label={DAY_NAME.format(utc(day))}
            disabled={min !== undefined && day < min}
            onClick={() => onPick(day)}
          >
            {Number(day.slice(8))}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * A day: typed ("24.10.2026", "24.10", "2026-10-24" — it counts as soon as it is a real day, owner remark 9)
 * or picked in the calendar. `value` and `onChange` use 'YYYY-MM-DD' ('' = none).
 */
export function DateField({ label, value, onChange, min, required }: { label: string; value: string; onChange: (day: string) => void; min?: string; required?: boolean }) {
  const [text, setText] = useState(formatDayText(value));
  const [open, setOpen] = useState(false);
  const [bad, setBad] = useState<string | null>(null);
  // the day before typing started: an unfinished text on leaving the field brings it back
  const before = useRef(value);
  const toggle = useRef<HTMLButtonElement>(null);
  const id = useId();
  // a change from outside (e.g. the last day following the first) shows up; what is being typed stays
  useEffect(() => {
    if (parseDayText(text, todayInWarsaw()) !== value) setText(formatDayText(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const close = () => {
    setOpen(false);
    toggle.current?.focus();
  };
  return (
    <div className="field datefield">
      <label htmlFor={id}>{label}</label>
      <div className="datefield-box">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          data-no-select-all=""
          placeholder="dd.mm.rrrr"
          autoComplete="off"
          required={required}
          aria-invalid={bad !== null || undefined}
          value={text}
          onFocus={() => {
            before.current = value;
          }}
          onChange={(e) => {
            setText(e.target.value);
            setBad(null);
            const day = parseDayText(e.target.value, todayInWarsaw());
            if (day && day !== value) onChange(day);
          }}
          onBlur={() => {
            if (text.trim() === '') {
              if (value !== '') onChange('');
              return;
            }
            const day = parseDayText(text, todayInWarsaw());
            if (day) {
              setText(formatDayText(day));
              return;
            }
            // e.g. "24.10.202": while typing it already counted as 24.10.2020 — undo that
            const back = before.current;
            if (back !== value) onChange(back);
            setText(formatDayText(back));
            setBad(text.trim());
          }}
        />
        <button ref={toggle} type="button" className="btn icon ghost datefield-open" aria-label={`Kalendarz: ${label}`} aria-expanded={open} onClick={() => setOpen(!open)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
            <path d="M4 6.5h16v13.5H4zM4 10.5h16M8.5 3.5v4M15.5 3.5v4" />
          </svg>
        </button>
        {open && (
          <CalendarPop
            label={label}
            value={value}
            {...(min ? { min } : {})}
            onClose={close}
            onPick={(day) => {
              onChange(day);
              setText(formatDayText(day));
              setBad(null);
              close();
            }}
          />
        )}
      </div>
      {bad !== null && (
        <span className="field-error">
          Nie rozpoznano daty „{bad}”{value ? ` — zostaje ${formatDayText(value)}` : ''}. Wpisz ją jako dd.mm.rrrr, np. 24.10.2026.
        </span>
      )}
    </div>
  );
}

export type Days = { firstDay: string; lastDay: string; firstTime: string; lastTime: string };

/** First and last day (the last follows the first until changed) and optional hours. */
export function DaysFields({ value, onChange }: { value: Days; onChange: (patch: Partial<Days>) => void }) {
  const [note, setNote] = useState('');
  /** A changed hour; an end before the start on one day moves the last day past midnight. */
  const changeTime = (patch: Partial<Days>) => {
    const moved = endPastMidnight({ ...value, ...patch });
    setNote(moved ? `Koniec po północy — ostatni dzień ustawiony na ${formatDayText(moved)}.` : '');
    onChange(moved ? { ...patch, lastDay: moved } : patch);
  };
  const changeDays = (patch: Partial<Days>) => {
    setNote('');
    onChange(patch);
  };
  return (
    <div className="days-fields">
      <div className="field-row">
        <DateField
          label="Pierwszy dzień"
          required
          value={value.firstDay}
          onChange={(day) => {
            const lastDay = lastDayAfterFirstChange({ oldFirst: value.firstDay, newFirst: day, last: value.lastDay });
            // an event past midnight stays past midnight when its first day moves
            const moved = value.lastDay !== value.firstDay ? endPastMidnight({ ...value, firstDay: day, lastDay }) : null;
            changeDays({ firstDay: day, lastDay: moved ?? lastDay });
          }}
        />
        <label className="field time-field">
          <span>Od godziny</span>
          <input type="time" aria-label="Godzina początku (opcjonalnie)" value={value.firstTime} onChange={(e) => changeTime({ firstTime: e.target.value })} />
        </label>
      </div>
      <div className="field-row">
        <DateField label="Ostatni dzień" required value={value.lastDay} {...(value.firstDay ? { min: value.firstDay } : {})} onChange={(day) => changeDays({ lastDay: day })} />
        <label className="field time-field">
          <span>Do godziny</span>
          <input type="time" aria-label="Godzina końca (opcjonalnie)" value={value.lastTime} onChange={(e) => changeTime({ lastTime: e.target.value })} />
        </label>
      </div>
      {note && (
        <p className="field-note" role="status">
          {note}
        </p>
      )}
      <p className="hint field-hint">Godziny są opcjonalne. Bez nich wydarzenie trwa całe dni. Koniec po północy (np. 02:00) to już następny dzień.</p>
    </div>
  );
}

/**
 * Days picked in the calendar with gaps (owner 2026-10-21 00:44: Shift / Cmd): the terms in words, the hours that every
 * term gets, and "Jeden ciągły termin" back to the plain first and last day.
 */
export function PickedDaysField({ days, firstTime, lastTime, onTimes, onSingle, error }: { days: readonly string[]; firstTime: string; lastTime: string; onTimes: (patch: { firstTime?: string; lastTime?: string }) => void; onSingle: () => void; error?: string | undefined }) {
  const runs = runsOf(days);
  const text = runs.map((r) => (r.first === r.last ? formatDay(r.first) : `${formatDay(r.first)} – ${formatDay(r.last)}`)).join(', ');
  return (
    <div className="days-fields picked-days" data-feature-id="events.segments">
      <p className="field">
        <span>Terminy ({runs.length})</span>
        <strong>{text}</strong>
        <span className="muted small">
          {new Set(days).size} dni. W przerwach sprzęt i ludzie są wolni; kosztorys liczy tylko te dni.
        </span>
        {error && <span className="field-error">{error}</span>}
      </p>
      <div className="field-row">
        <label className="field time-field">
          <span>Od godziny</span>
          <input type="time" aria-label="Godzina początku każdego terminu (opcjonalnie)" value={firstTime} onChange={(e) => onTimes({ firstTime: e.target.value })} />
        </label>
        <label className="field time-field">
          <span>Do godziny</span>
          <input type="time" aria-label="Godzina końca każdego terminu (opcjonalnie)" value={lastTime} onChange={(e) => onTimes({ lastTime: e.target.value })} />
        </label>
      </div>
      <p className="hint field-hint">Godziny dotyczą każdego terminu. Koniec przed początkiem (np. 16:00–02:00) to już następny dzień po ostatnim dniu terminu.</p>
      <button type="button" className="btn ghost small" onClick={onSingle}>
        Jeden ciągły termin
      </button>
    </div>
  );
}

/**
 * The second and further terms of an event (owner 2026-10-21 00:44): each with its days and hours and "Usuń termin";
 * "Dodaj termin" adds a day after the last term. The first term is the plain day fields above.
 */
export function MoreTermsField({ first, terms, onChange, onRemoveFirst }: { first: Days; terms: readonly Days[]; onChange: (terms: Days[]) => void; onRemoveFirst: () => void }) {
  const last = terms.at(-1) ?? first;
  const next = last.firstDay ? nextTermDay({ ...last, lastDay: last.lastDay || last.firstDay }) : addDays(todayInWarsaw(), 1);
  return (
    <div className="more-terms" data-feature-id="events.segments">
      {terms.length > 0 && (
        <button type="button" className="btn ghost small" onClick={onRemoveFirst}>
          Usuń termin 1
        </button>
      )}
      {terms.map((t, i) => (
        <fieldset key={i} className="term">
          <legend>Termin {i + 2}</legend>
          <DaysFields value={t} onChange={(patch) => onChange(terms.map((x, j) => (j === i ? { ...x, ...patch } : x)))} />
          <button type="button" className="btn ghost small" onClick={() => onChange(terms.filter((_, j) => j !== i))}>
            Usuń termin {i + 2}
          </button>
        </fieldset>
      ))}
      <button type="button" className="btn small" onClick={() => onChange([...terms, { firstDay: next, lastDay: next, firstTime: '', lastTime: '' }])}>
        Dodaj termin
      </button>
      <p className="hint field-hint">Kilka osobnych terminów to jedno wydarzenie: jeden kosztorys, jedna sprzętówka. W przerwach sprzęt i ludzie są wolni.</p>
    </div>
  );
}

/**
 * "Rezerwuj sprzęt także: [0] dni przed, [0] dni po" (owner 2026-10-21 06:47: gear is reserved only in the event's
 * terms; around them only by hand): days before the first term and after the last one, 0–30.
 */
export function ReserveAroundField({ before, after, onChange }: { before: number; after: number; onChange: (patch: { reserveDaysBefore?: number; reserveDaysAfter?: number }) => void }) {
  const num = (v: string) => Math.max(0, Math.min(30, Math.trunc(Number(v) || 0)));
  return (
    <fieldset className="reserve-around" data-feature-id="packlists.windows">
      <legend>Rezerwuj sprzęt także</legend>
      <div className="field-row">
        <label className="field">
          <span>dni przed</span>
          <input type="number" inputMode="numeric" min={0} max={30} aria-label="Rezerwuj sprzęt także: dni przed" value={before} onChange={(e) => onChange({ reserveDaysBefore: num(e.target.value) })} />
        </label>
        <label className="field">
          <span>dni po</span>
          <input type="number" inputMode="numeric" min={0} max={30} aria-label="Rezerwuj sprzęt także: dni po" value={after} onChange={(e) => onChange({ reserveDaysAfter: num(e.target.value) })} />
        </label>
      </div>
      <p className="hint field-hint">Sprzęt jest zarezerwowany tylko w terminie wydarzenia. Tu dodasz dni przed pierwszym terminem (pakowanie) i po ostatnim (zwrot). Pojedynczej pozycji możesz też ustawić własne dni w sprzętówce.</p>
    </fieldset>
  );
}

// ---------- client ----------

type Picked = { id: string; name: string };

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** "+ Nowy klient" (owner remark 1): a small form in a dialog, the new client is picked right away. */
function NewClientDialog({ initialName, onCreated, onClose }: { initialName: string; onCreated: (client: Picked) => void; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api<Picked>('/api/contractors', {
        method: 'POST',
        body: { kind: 'client', name: name.trim(), ...(phone.trim() ? { phone: phone.trim() } : {}), ...(email.trim() ? { email: email.trim() } : {}) },
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['contractors'] });
      onCreated({ id: created.id, name: created.name });
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (name.trim() !== '') save.mutate();
  }
  // owner 2026-10-23 21:14: Escape with a client typed and not added asks first
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ name, phone, email }, { name: initialName, phone: '', email: '' }),
    save: async () => {
      if (name.trim() === '') return false;
      await save.mutateAsync();
      return true;
    },
  });
  // in a portal: the dialog's form must not sit inside the event form (nested forms submit the outer one)
  return (
    <DialogPortal>
      <div className="dialog-backdrop">
        <form
          ref={guardRef}
          className="dialog panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-client-title"
          onSubmit={submit}
          onKeyDown={(e) => {
            if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
          }}
        >
            <DialogLayer onClose={onClose} busy={save.isPending} />
          <h2 id="new-client-title">Nowy klient</h2>
          <p className="muted">Resztę danych (NIP, adres) uzupełnisz później w Kontrahentach.</p>
          {error && (
            <p className="alert" role="alert">
              {fieldIssues(error).map((i) => i.message).join(' ') || error.message}
            </p>
          )}
          <label className="field">
            <span>Nazwa klienta</span>
            <input autoFocus required value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" placeholder="np. Live Nation Polska albo Filharmonia Bałtycka" />
          </label>
          <div className="field-row">
            <label className="field">
              <span>Telefon (opcjonalnie)</span>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
            </label>
            <label className="field">
              <span>E-mail (opcjonalnie)</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
            </label>
          </div>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={save.isPending || name.trim() === ''}>
              {save.isPending ? 'Zapisywanie…' : 'Dodaj klienta'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </DialogPortal>
  );
}

/** Client: search the contractors and pick one, or add a new client without leaving the form. */
export function ClientField({ picked, onPick, canCreate }: { picked: Picked | null; onPick: (client: Picked | null) => void; canCreate: boolean }) {
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const addButton = useRef<HTMLButtonElement>(null);
  const q = useDebounced(text.trim(), 250);
  const results = useQuery({
    queryKey: ['contractors', 'event-picker', q],
    enabled: q !== '',
    queryFn: () => api<{ contractors: Picked[] }>(`/api/contractors?q=${encodeURIComponent(q)}`),
  });
  const found = text.trim() !== '' ? results.data?.contractors : undefined;
  const shown = found?.slice(0, 8) ?? [];
  // owner 2026-10-30 21:56: the suggestion itself is the choice (no "Wybierz" button to hunt for); ↑/↓ and Enter from the search box
  const [active, setActive] = useState(-1);
  const listId = useId();
  const optionId = (i: number) => `${listId}-${i}`;
  useEffect(() => setActive(-1), [found]);
  const choose = (c: Picked) => {
    onPick({ id: c.id, name: c.name });
    setText('');
  };
  function onSearchKey(e: KeyboardEvent<HTMLInputElement>) {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && shown.length > 0) {
      e.preventDefault();
      setActive((a) => (e.key === 'ArrowDown' ? (a + 1) % shown.length : a <= 0 ? shown.length - 1 : a - 1));
    } else if (e.key === 'Enter' && shown[active]) {
      e.preventDefault();
      choose(shown[active]);
    }
  }
  return (
    <div className="client-field">
      {picked ? (
        <div className="client-picked">
          <span className="muted">Klient</span>
          <strong>{picked.name}</strong>
          <button type="button" className="btn small ghost" onClick={() => onPick(null)}>
            Usuń klienta
          </button>
        </div>
      ) : (
        <div className="field">
          <label htmlFor="client-search">Szukaj klienta</label>
          <div className="client-search">
            <input
              id="client-search"
              type="search"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={shown.length > 0}
              aria-controls={listId}
              aria-activedescendant={shown[active] ? optionId(active) : undefined}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Nazwa kontrahenta"
              autoComplete="off"
            />
            {canCreate && (
              <button ref={addButton} type="button" className="btn small accent-soft" onClick={() => setAdding(true)}>
                + Nowy klient
              </button>
            )}
          </div>
        </div>
      )}
      {!picked && found && found.length === 0 && <p className="muted">Nie ma takiego klienta.{canCreate ? ' Dodaj go przyciskiem „+ Nowy klient”.' : ''}</p>}
      {!picked && found && found.length > 0 && (
        <ul id={listId} role="listbox" className="list client-results" aria-label="Wyniki wyszukiwania klientów">
          {shown.map((c, i) => (
            <li
              key={c.id}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={`row client-option${i === active ? ' active' : ''}`}
              // keeps the focus in the search box
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => setActive(i)}
              onClick={() => choose(c)}
            >
              <strong>{c.name}</strong>
            </li>
          ))}
        </ul>
      )}
      {adding && (
        <NewClientDialog
          initialName={text.trim()}
          onClose={() => {
            setAdding(false);
            addButton.current?.focus();
          }}
          onCreated={(client) => {
            setAdding(false);
            setText('');
            onPick(client);
          }}
        />
      )}
    </div>
  );
}

// ---------- scopes ----------

export const SCOPES_KEY = ['events', 'scopes'] as const;

/** Custom scopes (names for every event) and, with the right to add them, `add`. */
export function useCustomScopes(canAdd: boolean): { custom: CustomScope[]; add: ((label: string) => Promise<CustomScope>) | null } {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: SCOPES_KEY, retry: false, queryFn: () => api<{ scopes: CustomScope[] }>('/api/events/scopes') });
  return {
    custom: list.data?.scopes ?? [],
    add: canAdd
      ? async (label) => {
          const scope = await api<CustomScope>('/api/events/scopes', { method: 'POST', body: { label } });
          await queryClient.invalidateQueries({ queryKey: SCOPES_KEY });
          return scope;
        }
      : null,
  };
}

/**
 * Scope pills (owner remarks 4, 5, 7): the everyday ones and the custom ones on top; light, stage, video and
 * adding an own scope under "Więcej zakresów".
 */
export function ScopePicker({ value, onChange, custom, onAdd }: { value: string[]; onChange: (scopes: string[]) => void; custom: CustomScope[]; onAdd?: ((label: string) => Promise<CustomScope>) | null }) {
  const groups = scopeGroups(custom, value);
  const [more, setMore] = useState(() => groups.more.some((s) => value.includes(s.code)));
  const [own, setOwn] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const toggle = (code: string) => onChange(value.includes(code) ? value.filter((s) => s !== code) : [...value, code]);
  const pill = (s: { code: string; label: string }) => (
    <button key={s.code} type="button" className="pill" aria-pressed={value.includes(s.code)} onClick={() => toggle(s.code)}>
      {s.label}
    </button>
  );
  async function addOwn() {
    if (!onAdd || own.trim() === '') return;
    setBusy(true);
    setError('');
    try {
      const scope = await onAdd(own.trim());
      if (!value.includes(scope.id)) onChange([...value, scope.id]);
      setOwn('');
    } catch (e) {
      setError(e instanceof ApiError ? fieldIssues(e).map((i) => i.message).join(' ') || e.message : 'Nie udało się dodać zakresu');
    } finally {
      setBusy(false);
    }
  }
  return (
    <fieldset className="scope-picker">
      <legend>Zakres prac</legend>
      <div className="pills">
        {groups.top.map(pill)}
        <button type="button" className="pill more" aria-expanded={more} onClick={() => setMore(!more)}>
          {more ? 'Mniej' : 'Więcej zakresów'}
        </button>
      </div>
      {more && (
        <div className="scope-more">
          <div className="pills">{groups.more.map(pill)}</div>
          {onAdd && (
            <div className="scope-own">
              <label className="visually-hidden" htmlFor="own-scope">
                Inny zakres
              </label>
              <input
                id="own-scope"
                type="text"
                value={own}
                placeholder="Inny zakres, np. tłumaczenia"
                autoComplete="off"
                onChange={(e) => setOwn(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void addOwn();
                  }
                }}
              />
              <button type="button" className="btn small" disabled={busy || own.trim() === ''} onClick={() => void addOwn()}>
                Dodaj zakres
              </button>
            </div>
          )}
          {onAdd && <p className="hint field-hint">Dodany zakres zostaje na górze listy dla kolejnych wydarzeń.</p>}
          {error && <span className="field-error">{error}</span>}
        </div>
      )}
    </fieldset>
  );
}

// ---------- choices ----------

function Segment<T extends string>({ legend, name, options, value, onChange, hint }: { legend: string; name: string; options: Record<T, string>; value: T | ''; onChange: (v: T) => void; hint: string }) {
  const hintId = useId();
  return (
    <fieldset className="radio-seg-field" aria-describedby={hintId}>
      <legend>{legend}</legend>
      <div className="radio-seg">
        {(Object.keys(options) as T[]).map((k) => (
          <label key={k} className={value === k ? 'on' : undefined}>
            <input type="radio" name={name} value={k} checked={value === k} onChange={() => onChange(k)} />
            {options[k]}
          </label>
        ))}
      </div>
      <p id={hintId} className="hint field-hint">
        {hint}
      </p>
    </fieldset>
  );
}

/** "Wstępna / Pewna" with what it means (owner remark 2). */
export function ReservationField({ value, onChange }: { value: ReservationKind; onChange: (v: ReservationKind) => void }) {
  return (
    <Segment
      legend="Rezerwacja sprzętu"
      name="reservationKind"
      options={RESERVATION_LABELS}
      value={value}
      onChange={onChange}
      hint="Wstępna nie blokuje zapisu, ale zajmuje sztuki w kolejności rezerwacji — kto doda ten sprzęt później, zobaczy brak. Pewna rezerwuje sztuki na wyłączność; gdy czegoś brakuje, od razu to zobaczysz."
    />
  );
}

/**
 * "System" (owner remark 3/13): which sound system plays, picked from the list the owner keeps. The small gear at
 * the right of the label (rule 9 of docs/wyglad.md) opens the list for whoever may change it.
 */
export function SystemField({ value, onChange, systems, onManage, error }: { value: string; onChange: (id: string) => void; systems: readonly SystemItem[]; onManage?: (() => void) | undefined; error?: string | undefined }) {
  const id = useId();
  const chosen = systems.find((s) => s.id === value);
  const about = chosen?.kind === 'external' && chosen.contractorName ? `Dostawca: ${chosen.contractorName}` : chosen?.kind === 'own' && chosen.blockName ? `Blok do sprzętówki: ${chosen.blockName}` : '';
  return (
    <div className="field system-field" data-feature-id="events.systems">
      <div className="field-head">
        <label htmlFor={id}>System</label>
        {onManage && <RowMenu icon="gear" label="Ustawienia: lista systemów" featureId="events.systems" actions={[{ label: 'Zarządzaj systemami…', onSelect: onManage }]} />}
      </div>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={`${id}-hint`}>
        <option value="">Wybierz system</option>
        {systemGroups(systems, value).map((g) =>
          g.label ? (
            <optgroup key={g.label} label={g.label}>
              {g.items.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </optgroup>
          ) : (
            g.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))
          ),
        )}
      </select>
      {about && <span className="field-note">{about}</span>}
      {chosen?.deleted && <span className="field-note">Ten system usunięto z listy — wydarzenie go zachowuje.</span>}
      <span id={`${id}-hint`} className="hint field-hint">
        Jaki system gra na wydarzeniu. Nowy (np. dopożyczony L-Acoustics L2 od Szmyta) dodasz trybikiem ⚙ obok albo w Administracja → Ustawienia firmy.
      </span>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

const SOURCES = [DEFAULT_EQUIPMENT_SOURCE, 'Nasz magazyn + dopożyczenie', 'Sprzęt klienta', 'Sprzęt obiektu', NO_GEAR_SOURCE];

/** "Skąd sprzęt" with a short explanation and quick answers (owner remark 3). */
export function EquipmentSourceField({ value, onChange, error }: { value: string; onChange: (v: string) => void; error?: string | undefined }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>Skąd sprzęt</label>
      <input id={id} value={value} onChange={(e) => onChange(e.target.value)} autoComplete="off" aria-describedby={`${id}-hint`} placeholder="np. Nasz magazyn + dopożyczenie z Audio-Rent" />
      <div className="pills small" aria-label="Częste odpowiedzi" role="group">
        {SOURCES.map((s) => (
          <button key={s} type="button" className="pill" aria-pressed={value === s} onClick={() => onChange(s)}>
            {s}
          </button>
        ))}
      </div>
      <span id={`${id}-hint`} className="hint field-hint">
        Skąd weźmiemy sprzęt na to wydarzenie: z naszego magazynu, dopożyczony (napisz od kogo), sprzęt klienta albo obiektu. „Bez sprzętu” — nowe wydarzenie powstaje bez kroku sprzętówki.
      </span>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

/** "Potrzebna wycena" as a highlighted switch (owner remark 6). */
export function QuoteSwitch({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className={`quote-switch${value ? ' on' : ''}`}>
      <button type="button" role="switch" aria-checked={value} aria-label="Potrzebna wycena" onClick={() => onChange(!value)}>
        <span className="switch-track" aria-hidden="true">
          <span className="switch-thumb" />
        </span>
      </button>
      <div>
        <strong>Potrzebna wycena</strong>
        <span className="hint">Wydarzenie trafi na górę listy „Wyceny” jako do zrobienia.</span>
      </div>
    </div>
  );
}

/**
 * "Odpowiedzialny" (P16, owner point 3): the person responsible for the event — the quote, the gear, talks with the
 * client, the schedule. They see the event with scope "własne" and get its reminders. The list comes from the
 * server only for whoever adds or edits events; until it loads, the current person is kept by name.
 */
export function ResponsibleField({ value, currentName, onChange }: { value: string; currentName: string | null; onChange: (id: string) => void }) {
  const people = useQuery({ queryKey: ['events', 'responsible-candidates'], queryFn: () => api<{ users: Array<{ id: string; name: string }> }>('/api/events/responsible-candidates'), staleTime: 60_000 });
  const users = people.data?.users ?? [];
  const known = value === '' || users.some((u) => u.id === value);
  return (
    <label className="field" data-feature-id="events.responsible">
      <span>Odpowiedzialny</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— nikt —</option>
        {!known && <option value={value}>{currentName ?? 'inna osoba'}</option>}
        {users.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name}
          </option>
        ))}
      </select>
      <span className="hint">Wycena, zapotrzebowanie, rozmowy z klientem, harmonogram. Widzi to wydarzenie i dostaje jego przypomnienia.</span>
    </label>
  );
}
